import { useState } from 'react';
import classNames from 'classnames';
import { t } from 'ttag';

import {
  ProductUserStatus,
  ProductVisibleStatus,
  SellingConditionEnum,
  TagsEnum,
} from '@models';
import { useIsClient } from '@utils/common';
import { useDesktopOrWider } from '@utils/mediaQuery';
import { useUserSubscriptionInfo } from '@hook/useUserSubscriptionInfo';
import SlickSlider from '@ui/SlickSlider';
import { Image } from '@ui/Image';
import { FullScreenCarousel } from '@ui/FullScreenCarousel';
import { ProductDetailsLabels } from '@components/ProductDetailsLabels';
import { ProductDetailsSimilarProductsLink } from '@components/ProductDetailsSimilarProductsLink';

import s from './ProductDetailsPhotos.sss';

interface Photo {
  idStr?: string | null,
  thumbnailUrl: string,
  originalUrl: string
}

interface Product {
  id: number,
  price?: number | null,
  photos: Photo[],
  withSafePayment: boolean,
  isUkrainian: boolean,
  isOwner: boolean,
  sellingCondition: SellingConditionEnum,
  tags: TagsEnum[],
  statusTitle?: ProductVisibleStatus | null,
  userStatusTitle: ProductUserStatus,
}

interface Props {
  className?: string,
  photos: Photo[],
  product: Product,
}

export const ProductDetailsPhotos: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  photos,
  product,
}) => {
  const isDesktop = useDesktopOrWider();
  const isClient = useIsClient();
  const [photoIndex, setPhotoIndex] = useState(-1);

  const {
    viewer,
    smartInfo,
    loading: smartInfoLoading,
  } = useUserSubscriptionInfo({
    context: {
      batch: true,
    },
  });

  const restPhotos = photos.slice(1);
  const photosCount = photos?.length || 0;
  const originalPhotos = photos.map((photo) => photo.originalUrl) || [];
  const currentGalleyPhoto = originalPhotos[photoIndex];
  const nextGalleryPhoto = originalPhotos[(photoIndex + 1) % photosCount];
  const prevGalleryPhoto = originalPhotos[(photoIndex + photosCount - 1) % photosCount];

  const isSwapLabelVisible = product.sellingCondition === SellingConditionEnum.Exchange;
  let isFreeDeliveryLabelVisible = false;
  let isSmartLabelVisible = false;

  let statusCaption: string | null = null;

  if (product.userStatusTitle === ProductUserStatus.Hidden) {
    statusCaption = t`Деактивований`;
  }

  if (product.statusTitle === ProductVisibleStatus.Sold) {
    statusCaption = t`Проданий`;
  }

  if (!smartInfoLoading) {
    isSmartLabelVisible = (
      !product.isOwner
      && !isSwapLabelVisible
      && !!viewer?.subscriptionInfo
      && Number(product.price) >= smartInfo?.minPurchaseCondition
    );

    isFreeDeliveryLabelVisible = product.tags.includes(TagsEnum.FreeShipping) && !isSmartLabelVisible;
  }

  const handleNextGalleryPhoto = () => {
    setPhotoIndex(
      (prevIndex) => (prevIndex + 1) % photosCount,
    );
  };

  const handlePrevGalleryPhoto = () => {
    setPhotoIndex(
      (prevIndex) => (prevIndex + photosCount - 1) % photosCount,
    );
  };

  const handleCloseGallery = () => {
    setPhotoIndex(-1);
  };

  return (
    <>
      <div className={classNames(s.root, className)}>
        {!!restPhotos.length && (
          <ul className={classNames(s.list, s.restPhotos)}>
            {restPhotos.map((photo, i) => (
              <li
                key={i}
                className={s.item}
              >
                <div
                  key={i}
                  className={s.photoContainer}
                >
                  <Image
                    className={s.photo}
                    src={photo.thumbnailUrl}
                    lazy={true}
                    onClick={() => setPhotoIndex(i + 1)}
                    alt='Product photo'
                    // esputnik required attribute
                    data-product-photo={photo.thumbnailUrl}
                  />
                  {i === 6 && (
                    <span className={s.counter}>
                      {'+3'}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className={s.sliderContainer}>
          <SlickSlider
            className={s.slider}
            speed={400}
            slidesToShow={1}
            slidesToScroll={1}
            arrows={false}
            infinite={false}
            variableWidth={false}
            swipeToSlide={!isDesktop}
            swipe={!isDesktop}
            dots={isClient && !isDesktop && photos.length > 1}
          >
            {photos.map((photo, i) => (
              <div
                key={i}
                className={s.photoContainer}
              >
                <Image
                  className={s.photo}
                  src={photo.thumbnailUrl}
                  lazy={i > 0}
                  fetchPriority={i === 0 ? 'high' : 'auto'}
                  alt='Product photo'
                  onClick={() => setPhotoIndex(i)}
                  // esputnik required attribute
                  data-product-photo={photo.thumbnailUrl}
                />
              </div>
            ))}
          </SlickSlider>

          <div className={s.labels}>
            <ProductDetailsLabels
              isSafePayment={product.withSafePayment}
              isMadeInUkraine={product.isUkrainian}
              isFreeDelivery={isFreeDeliveryLabelVisible}
              isSmart={isSmartLabelVisible}
              isSwap={isSwapLabelVisible}
            />
          </div>

          {!!statusCaption && (
            <p className={s.statusCaption}>
              {statusCaption}
            </p>
          )}

          <ProductDetailsSimilarProductsLink className={s.similar} />
        </div>
      </div>

      {currentGalleyPhoto && (
        <FullScreenCarousel
          mainSrc={currentGalleyPhoto}
          nextSrc={nextGalleryPhoto}
          prevSrc={prevGalleryPhoto}
          onCloseRequest={handleCloseGallery}
          onMoveNextRequest={handleNextGalleryPhoto}
          onMovePrevRequest={handlePrevGalleryPhoto}
        />
      )}
    </>
  );
};
